import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { User } from '../App';
import { ShareModal } from './ShareModal';
import { toast } from 'sonner';
import { i18n } from '../utils/i18n';
import { 
  Users, 
  Gift, 
  TrendingUp, 
  Trophy, 
  Copy,
  Settings as SettingsIcon,
  ArrowLeft,
  QrCode,
  X
} from 'lucide-react';

interface ReferralUser {
  id: number;
  nickname: string;
  referralCode: string;
  joinDate: string;
  totalEarnings: number;
  level: number;
}

interface RewardsProps {
  user: User;
  onSettingsClick: () => void;
}

export function Rewards({ user, onSettingsClick }: RewardsProps) {
  // user가 없으면 로딩 화면 표시
  if (!user) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>{i18n.t('loadingUserInfo')}</p>
        </div>
      </div>
    );
  }

  const [activeTab, setActiveTab] = useState<'rewards' | 'assets'>('rewards');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  // 새로 추가된 상태들
  const [referralUsers, setReferralUsers] = useState<ReferralUser[]>([]);
  const [assetHistory, setAssetHistory] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // 추천인 리스트 로드
  useEffect(() => {
    const loadReferrals = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('vatton-jwt-token');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/rewards/referrals/${user.walletAddress}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setReferralUsers(data);
        }
      } catch (error) {
        console.error('추천인 데이터 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user.walletAddress) {
      loadReferrals();
    }
  }, [user.walletAddress]);

  // 자산 변경 내역 로드
  useEffect(() => {
    const loadAssetHistory = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('vatton-jwt-token');
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/rewards/asset-history/${user.walletAddress}?limit=20`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAssetHistory(data);
        }
      } catch (error) {
        console.error('자산 내역 로드 실패:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user.walletAddress) {
      loadAssetHistory();
    }
  }, [user.walletAddress]);

  // 추천 코드 복사
  const copyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(user.referralCode || '');
      toast.success('Referral code copied!');
    } catch (error) {
      console.error('복사 실패:', error);
      toast.error('Failed to copy code');
    }
  };

  // QR 코드 표시
  const showReferralQR = () => {
    setQrCodeUrl(`http://159.223.63.184:3501/?ref=${user.referralCode}`);
    setShowQRModal(true);
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="px-4 py-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-light text-foreground">Rewards</h1>
            <p className="text-muted-foreground">Manage your referrals and earnings</p>
          </div>
          <button
            onClick={onSettingsClick}
            className="w-10 h-10 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
          >
            <SettingsIcon className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex bg-muted mx-4 mt-4 mb-4 rounded-xl p-1">
        <button
          onClick={() => setActiveTab('rewards')}
          className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
            activeTab === 'rewards'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Gift className="w-4 h-4" />
            <span className="text-sm font-medium">Rewards</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('assets')}
          className={`flex-1 py-3 text-center rounded-lg transition-all duration-200 ${
            activeTab === 'assets'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">Asset Management</span>
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto px-4 pb-4">
        {activeTab === 'rewards' ? (
          <div className="space-y-6">
            {/* My Referral Code */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Users className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-bold text-foreground mb-2">My Referral Code</h3>
                  <div className="bg-gradient-to-r from-orange-100 to-red-100 dark:from-orange-900/30 dark:to-red-900/30 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-center space-x-3 mb-2">
                      <div className="text-3xl font-bold text-orange-600">{user.referralCode || 'N/A'}</div>
                      <Button
                        onClick={showReferralQR}
                        variant="ghost"
                        size="sm"
                        className="p-1 h-auto"
                      >
                        <QrCode className="w-6 h-6 text-orange-600" />
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-sm">Share this code with friends</p>
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      onClick={copyReferralCode}
                      variant="outline"
                      className="flex-1"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Code
                    </Button>
                    <Button
                      onClick={() => setShowShareModal(true)}
                      className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Share
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-1">100</div>
                  <p className="text-blue-800 dark:text-blue-300 text-sm">Total Referrals (VTTN)</p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
                <CardContent className="p-4 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-1">130</div>
                  <p className="text-green-800 dark:text-green-300 text-sm">Total Earned (VTTN)</p>
                </CardContent>
              </Card>
            </div>

            {/* Referral Fee Section */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <h3 className="font-bold text-foreground mb-4">Referral Fee</h3>
                <p className="text-muted-foreground text-sm mb-4">{i18n.t('referralFeeInfo')}</p>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-foreground">{i18n.t('referrerBonus')}</span>
                    <span className="font-semibold text-blue-600">100 VTTN</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-foreground">{i18n.t('refereeBonus')}</span>
                    <span className="font-semibold text-green-600">0 VTTN</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-foreground font-semibold">{i18n.t('totalReferralReceived')}</span>
                    <span className="font-bold text-orange-600">100 VTTN</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Referral Tree Section */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <h3 className="font-bold text-foreground mb-4">Referral Tree</h3>
                <p className="text-muted-foreground text-sm mb-4">People who used your code</p>
                
                <div className="space-y-3">
                  <div className="text-center py-8">
                    <div className="text-6xl mb-4">🌳</div>
                    <p className="text-muted-foreground">No referrals yet</p>
                    <p className="text-muted-foreground text-sm">Start sharing your code!</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Current Rank */}
            <Card className="bg-gradient-to-br from-yellow-50 to-orange-100 dark:from-yellow-900/20 dark:to-orange-800/20 border-yellow-200 dark:border-yellow-800">
              <CardContent className="p-4 text-center">
                <div className="flex items-center justify-center mb-4">
                  <Trophy className="w-8 h-8 text-yellow-600 mr-2" />
                  <h3 className="font-bold text-yellow-800 dark:text-yellow-300">Current Rank</h3>
                </div>
                <p className="text-yellow-700 dark:text-yellow-400 text-sm mb-2">Your referral tier</p>
                <div className="text-4xl font-bold text-yellow-600 mb-2">Bronze</div>
                <div className="w-full bg-yellow-200 dark:bg-yellow-800 rounded-full h-2 mb-2">
                  <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '15.2%' }}></div>
                </div>
                <p className="text-yellow-700 dark:text-yellow-400 text-sm">15.2% progress</p>
                <p className="text-yellow-600 dark:text-yellow-500 text-xs mt-2">Next: Silver (need 5 more referrals)</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Portfolio Overview */}
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-100 dark:from-indigo-900/20 dark:to-purple-800/20 border-indigo-200 dark:border-indigo-800">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-indigo-600 mb-2">
                  {user.balance.toFixed(2)} VTTN
                </div>
                <p className="text-muted-foreground text-sm">≈ ${(user.balance * 0.5).toFixed(2)}</p>
              </CardContent>
            </Card>

            {/* Asset Breakdown */}
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <h3 className="font-bold text-foreground mb-4">Asset Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-3 border-b border-border">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">VT</span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">VTTN</p>
                        <p className="text-muted-foreground text-sm">Vatton Token</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-foreground">{user.balance.toFixed(2)} VTTN</p>
                      <p className="text-muted-foreground text-sm">≈ ${(user.balance * 0.5).toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <ShareModal
          user={user}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl p-6 max-w-sm w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-foreground">Referral QR Code</h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="w-8 h-8 bg-muted rounded-full flex items-center justify-center hover:bg-accent transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
            <div className="text-center">
              <div className="w-48 h-48 bg-white rounded-xl mx-auto mb-4 flex items-center justify-center border-2 border-border">
                <QrCode className="w-32 h-32 text-gray-400" />
              </div>
              <p className="text-muted-foreground text-sm">Scan to join with referral code</p>
              <p className="font-mono text-foreground mt-2">{user.referralCode}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
